import type { FC } from "react"
import type { GetServerSideProps, GetServerSidePropsResult } from "next"

import CreateProposal from "createProposal/components/CreateProposal"
import { isValidAccountId } from "web3/helpers/accountId"
import { fetchGovernanceById } from "governance/helpers/fetch"
import { ROUTES } from "common/constants/routes"
import {
  fetchGovernorsByOrganization,
  fetchOrganizationBySlug,
} from "organization/helpers/fetcher"
import type { MeQuery } from "query/graphql"
import {
  GovernorKind,
  GovernorType,
  MeDocument,
  type Governor,
  type Organization,
} from "query/graphql"
import { BREADCRUMB } from "common/constants/breadcrumb"
import ErrorBoundary from "common/components/ErrorBoundary"
import GovernanceProvider from "governance/context"
import MainLayout from "ui/layouts/main/MainLayout"
import MainLayoutHead from "ui/layouts/main/MainLayoutHead"
import MainLayoutContainer from "ui/layouts/main/MainLayoutContainer"
import CreateProposalGovernanceSelector from "createProposal/components/legacy/CreateProposalGovernanceSelector"
import MainLayoutFooter from "ui/layouts/main/MainLayoutFooter"
import { CreateProposalProvider } from "createProposal/providers/CreateProposalProvider"
import { isCorrectSlug, isWhitelabel } from "whitelabel/hooks/useIsWhiteLabel"
import {
  getWhiteLabelRoute,
  getWhitelabelBreadcrumb,
} from "whitelabel/utils/breadcrumb"
import { getSessionToken } from "common/helpers/cookies"
import {
  getGovernanceIds,
  getGovernorsByKind,
} from "governance/helpers/governance"
import MainLayoutHeader from "ui/layouts/main/MainLayoutHeader"
import { OrganizationMenu } from "ui/layouts/main/header/HeaderDAO"
import { OrganizationProvider } from "organization/providers/OrganizationProvider"
import DaoLayoutBreadcrumb from "ui/layouts/dao/DaoLayoutBreadcrumb"
import { getOrganizationSocialPreviewImage } from "organization/helpers/organization"
import { isSolanaAddress } from "web3/helpers/address"
import { Chain } from "web3/hooks/useMultichainAccount"
import { fetcher } from "common/helpers/fetcher"

type Props = {
  governors: Governor[]
  organization: Organization
  isWhiteLabel: boolean
}

const CreateNewProposal: FC<Props> = ({
  governors,
  organization,
  isWhiteLabel,
}) => {
  const [governor] = governors
  const { id: governanceId } = governor
  const { slug, name } = organization
  const governanceIds = getGovernanceIds(governors)
  const isMultiGov = governanceIds.length > 1

  const title = isWhiteLabel
    ? `${name} | Create Proposal`
    : `Tally | ${name} Create Proposal`
  const preview = {
    title,
    image: getOrganizationSocialPreviewImage(organization),
    description: "",
  }

  const breadcrumb = isWhiteLabel
    ? getWhitelabelBreadcrumb(
        BREADCRUMB.governance.create(slug, name, isWhiteLabel),
      )
    : BREADCRUMB.governance.create(slug, name, isWhiteLabel)

  return (
    <ErrorBoundary section="NewProposal">
      <OrganizationProvider organization={organization}>
        <GovernanceProvider governor={governor}>
          <MainLayout>
            <MainLayoutHead preview={preview} title={title} />
            <MainLayoutHeader
              activeOrganizationMenu={OrganizationMenu.Proposals}
              isWhiteLabel={isWhiteLabel}
              organization={organization}
            />
            <DaoLayoutBreadcrumb breadcrumb={breadcrumb} />
            <MainLayoutContainer>
              {isMultiGov ? (
                <ErrorBoundary section="CreateProposalGovernanceSelector">
                  <CreateProposalGovernanceSelector
                    isActive
                    organizationId={organization.id}
                    slug={slug}
                  />
                </ErrorBoundary>
              ) : null}

              {!isMultiGov ? (
                <ErrorBoundary section="NewProposal">
                  <CreateProposalProvider
                    governanceId={governanceId}
                    governanceSlug={slug}
                  >
                    <CreateProposal
                      governanceSlug={slug}
                      isWhiteLabel={isWhiteLabel}
                      organizationId={organization.id}
                    />
                  </CreateProposalProvider>
                </ErrorBoundary>
              ) : null}
            </MainLayoutContainer>
            <MainLayoutFooter isWhiteLabel={isWhiteLabel} />
          </MainLayout>
        </GovernanceProvider>
      </OrganizationProvider>
    </ErrorBoundary>
  )
}

export const getServerSideProps: GetServerSideProps = async (
  context,
): Promise<GetServerSidePropsResult<Props>> => {
  const { query } = context
  const { governanceId: governanceIdParam } = query
  const slug = governanceIdParam as string

  // Find governance by id and redirect to /gov/:slug
  if (isValidAccountId(slug)) {
    const governance = await fetchGovernanceById(slug)

    if (governance && governance.organization) {
      return {
        redirect: {
          permanent: false,
          destination: ROUTES.governance.proposal.create(
            governance.organization.slug,
          ),
        },
      }
    }
  }

  if (isWhitelabel(context)) {
    if (!isCorrectSlug(context, slug)) {
      return {
        redirect: {
          permanent: false,
          destination: "/404",
        },
      }
    }
  }

  const sessionToken = getSessionToken(context)

  if (!sessionToken) {
    return {
      redirect: {
        permanent: false,
        destination: ROUTES.user.connect(
          isWhitelabel(context)
            ? getWhiteLabelRoute(ROUTES.governance.proposal.create(slug))
            : ROUTES.governance.proposal.create(slug),
          Chain.EVM,
        ),
      },
    }
  }

  const fetchMe = async () => {
    return fetcher.gql<MeQuery>({
      query: MeDocument,
      context,
    })
  }

  const meMetadata = await fetchMe()

  if (!meMetadata?.me?.address || isSolanaAddress(meMetadata.me.address)) {
    return {
      redirect: {
        permanent: false,
        destination: ROUTES.user.connect(
          isWhitelabel(context)
            ? getWhiteLabelRoute(ROUTES.governance.proposal.create(slug))
            : ROUTES.governance.proposal.create(slug),
          Chain.EVM,
        ),
      },
    }
  }

  const organization = await fetchOrganizationBySlug(slug, context)

  if (!organization) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  const _governors = await fetchGovernorsByOrganization(
    organization.id,
    context,
  )

  if (!_governors.length) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  // Get governors array with only the ones where a proposal can be created
  const governors = getGovernorsByKind(_governors, [
    GovernorKind.Single,
    GovernorKind.Multiprimary,
    GovernorKind.Multisecondary,
    GovernorKind.Hub,
  ]).filter((governor) => governor.type !== GovernorType.Spoke)

  if (!governors.length) {
    return {
      redirect: {
        permanent: false,
        destination: "/404",
      },
    }
  }

  return {
    props: {
      organization,
      governors,
      isWhiteLabel: isWhitelabel(context),
    },
  }
}

export default CreateNewProposal
